import { useCallback, useMemo } from "react";
import { GlBasis } from "@common/schemas/accounting-integration.schema";
import {
    formatPeriod,
    ROLE_LABELS,
    unrecognizedMessageForSource,
} from "@components/accounting/migrationFileUploadShared";
import {
    applyMigrationResolutionAction,
    migrationResolutionOptions,
    type MigrationResolutionAction,
} from "@components/accounting/migrationRowResolution";
import type {
    MigrationMatchedRow,
    MigrationRowStatus,
    MigrationUnverifiedDoc,
} from "@components/accounting/migrationFileMatching.types";
import type { CoADocumentMigrationSource } from "@common/schemas/accounting-integration.schema";

const BASIS_OPTIONS: GlBasis[] = ["cashGl", "accrualGl"];

interface UseMigrationUploadGridsParams {
    source: CoADocumentMigrationSource;
    displayRows: MigrationMatchedRow[];
    unverifiedDocs: MigrationUnverifiedDoc[];
    onRemoveDoc: (documentId: number) => void;
    onSelectBasis: (documentId: number, basis: GlBasis) => void;
    onDismissParserWarning: (documentId: number) => void;
    isMigrating: boolean;
    isCompleted: boolean;
}

export function useMigrationUploadGrids({
    source,
    displayRows,
    unverifiedDocs,
    onRemoveDoc,
    onSelectBasis,
    onDismissParserWarning,
    isMigrating,
    isCompleted,
}: UseMigrationUploadGridsParams) {
    const unrecognizedFallback = unrecognizedMessageForSource(source);
    const isRowActionDisabled = isMigrating || isCompleted;

    const buildMigrationRowActions = useCallback(
        ({
            documentId,
            status,
            isUnknownBasis,
        }: {
            documentId: number;
            status: MigrationRowStatus | MigrationUnverifiedDoc["status"];
            isUnknownBasis?: boolean;
        }) => {
            const resolutionOptions = migrationResolutionOptions({
                status,
                isUnknownBasis,
                basisOptions: BASIS_OPTIONS,
            });
            return {
                resolutionOptions,
                onResolution: (action: MigrationResolutionAction) =>
                    applyMigrationResolutionAction({
                        documentId,
                        action,
                        onSelectBasis,
                        onDismissParserWarning,
                    }),
            };
        },
        [onSelectBasis, onDismissParserWarning],
    );

    const mainData = useMemo(
        () =>
            displayRows.map((row, index) => ({
                rowNumber: index + 1,
                roleLabel: ROLE_LABELS[row.role],
                periodLabel: formatPeriod(row.period),
                fileName: row.fileName,
                status: row.status,
                summary: row.summary,
                statusMessage:
                    row.statusMessage ??
                    (row.status === "unrecognized"
                        ? unrecognizedFallback
                        : undefined),
                rejection: row.rejection,
                documentId: row.documentId,
                ...(row.documentId == null
                    ? {}
                    : buildMigrationRowActions({
                          documentId: row.documentId,
                          status: row.status,
                          isUnknownBasis: row.isUnknownBasis,
                      })),
                onRemove: onRemoveDoc,
                disabled: isRowActionDisabled,
            })),
        [
            displayRows,
            unrecognizedFallback,
            buildMigrationRowActions,
            onRemoveDoc,
            isRowActionDisabled,
        ],
    );

    const unverifiedData = useMemo(
        () =>
            unverifiedDocs.map((doc) => ({
                _empty: null,
                roleLabel: "",
                periodLabel: formatPeriod(doc.period),
                fileName: doc.fileName,
                status: doc.status,
                statusMessage:
                    doc.message ??
                    (doc.status === "unrecognized"
                        ? unrecognizedFallback
                        : undefined),
                rejection: doc.rejection,
                documentId: doc.documentId,
                ...buildMigrationRowActions({
                    documentId: doc.documentId,
                    status: doc.status,
                    isUnknownBasis: doc.isUnknownBasis,
                }),
                onRemove: onRemoveDoc,
                disabled: isRowActionDisabled,
            })),
        [
            unverifiedDocs,
            unrecognizedFallback,
            buildMigrationRowActions,
            onRemoveDoc,
            isRowActionDisabled,
        ],
    );

    return { mainData, unverifiedData };
}
